<template lang="pug">
section.section
	.login-app
		.login-form
			TechnicalError(v-if="technicalError")
			template(v-else)
				h1.sf-heading-1.text-center Bejelentkezés
				VueFormNotification(v-if="loginError", type="error", icon-visible="both")
					template(slot="title") Hibás törzsszám vagy jelszó!
					p.sf-text Ha háromszor egymás után rossz jelszót adsz meg, 24 órára blokkoljuk fiókodat.
				VueFormNotification(v-if="accountBlocked", type="error", icon-visible="both")
					template(slot="title") Fiókodat 24 órára blokkoltuk!
					p.sf-text Háromszor egymás után hibás jelszót adtál meg, ezért fiókodat 24 órára blokkoltuk. Próbálkozz újra 24 óra múlva a korábban megadott jelszóval vagy #[a.sf-link(:href="forgottenPasswordUrl") igényelj új jelszót] a belépeshez!
				VueFormNotification(v-if="statusError", type="error", icon-visible="both")
					template(slot="title") Hibás adatok!
					p.sf-text A megadott adatokkal a belépés nem lehetséges!
				form(method="post", action="/partner/auth/login", @submit="submitLoginForm")
					input(type="hidden", name="captchaKey", :value="captcha.captchaKey")
					.form-row
						.form-col
							.label-tooltip-wrapper
								label#login-form-prime-number-label(for="login-form-prime-number-input") Törzsszám
								VueTooltip(placement="bottom")
									p(slot="content") 13 jegyből álló partneri törzsszám.
						.form-col
							VueMaskedInput#login-form-prime-number-input(
								v-model="model.username",
								type="text",
								name="username",
								:mask="'#############'",
								:max-length="13",
								:has-error="hasError('model.username')",
								@blur="validateField('model.username')",
								:aria-describedby="hasError('model.username') ? 'login-form-prime-number-error' : 'login-form-prime-number-label'"
							)
							VueErrorMessage#login-form-prime-number-error(v-if="hasError('model.username')", aria-live="assertive", position="bottom")
								template(v-if="hasError('model.username', 'required')") Kérjük, adj meg egy törzsszámot!
								template(v-if="hasError('model.username', 'numeric') || hasError('model.username', 'minLength') || hasError('model.username', 'maxLength')") Kérjük, ellenőrizd a törzsszámot!
					.form-row
						.form-col
							label#login-form-password-input-label(for="login-form-password-input") Jelszó
						.form-col
							VuePasswordInput#login-form-password-input(
								v-model="model.password",
								name="password",
								:max-length="20",
								:has-error="hasError('model.password')",
								@blur="validateField('model.password')",
								:aria-describedby="hasError('model.password') ? 'login-form-password-error' : 'login-form-password-input-label'"
							)
							VueErrorMessage#login-form-password-error(v-if="hasError('model.password')", aria-live="assertive", position="bottom")
								template(v-if="hasError('model.password', 'required')") Kérjük, adj meg egy jelszót!
								template(v-if="hasError('model.password', 'maxLength')") Kérjük, ellenőrizze a jelszót!
							a.sf-link.login-form__forgotten-pass-link(:href="forgottenPasswordUrl") Elfelejtett jelszó
					.form-row(v-if="captcha.htmlFragment")
						.form-col
							label#login-form-captcha-label(for="login-form-captcha-input") Ellenőrzőkód
						.form-col
							VueCaptcha#login-form-captcha-input(
								v-model="model.captchaText",
								name="captchaText",
								:has-error="captchaError || hasError('model.captchaText')",
								:img="`data:image/jpeg;base64,${captcha.htmlFragment}`",
								:max-length="5",
								:max-width="110",
								:max-img-width="120",
								@keyup="resetCaptchaError",
								@blur="validateField('model.captchaText')",
								:aria-describedby="captchaError || hasError('model.captchaText') ? 'login-form-captcha-error' : 'login-form-captcha-label'"
							)
							VueErrorMessage#login-form-captcha-error(v-if="captchaError || hasError('model.captchaText')", aria-live="assertive", position="bottom")
								template(v-if="hasError('model.captchaText', 'required')") Kérjük, adja meg a képen látható kódot!
								template(v-if="!hasError('model.captchaText') && captchaError") Kérjük, ellenőrizze a kódot!
					.form-row
						VueButton(type="submit", :disabled="$v.$invalid || sendingForm") Belépés
				h3.sf-heading-3 Először jársz itt?
				p.sf-text Ha még nem használtad a Partner Portált, a Regisztrálok linkre kattintva kezdheted el a használatát, néhány adat megadása után.
				p.sf-text
					a.sf-link.login-form__registration-link(:href="registrationUrl") Regisztrálok
</template>

<script>
import VueCaptcha from '@ui-common/vue-captcha';
import VueFormNotification from '@ui-common/vue-form-notification';
import VueMaskedInput from '@ui-common/vue-masked-input';
import VuePasswordInput from '@ui-common/vue-password-input';
import VueErrorMessage from '@ui-common/vue-error-message';
import { required, numeric, minLength, maxLength } from 'vuelidate/lib/validators';
import { jumpToFirstError } from '../../common/js/find-first-error-component/find-first-error-component';
import { errorHandlerUtils } from '../../common/js/error-handler-utils/error-handler-utils';
import query from 'querystring-component';
import TechnicalError from '../../common/components/technical-error/technical-error';
import { ERROR_STATUSES } from '../../common/js/constants/constants';

export default {
	mixins: [jumpToFirstError, errorHandlerUtils],
	data: () => ({
		loginError: false,
		accountBlocked: false,
		statusError: false,
		technicalError: false,
		captcha: {
			captchaKey: null,
			htmlFragment: null
		},
		model: {
			username: null,
			password: null,
			captchaText: null
		},
		captchaError: false,
		sendingForm: false
	}),
	props: {
		registrationUrl: {
			required: false
		},
		forgottenPasswordUrl: {
			required: false
		}
	},
	validations() {
		return {
			model: {
				username: { required, numeric, minLength: minLength(13), maxLength: maxLength(13) },
				password: { required, maxLength: maxLength(20) },
				captchaText: this.captcha.htmlFragment ? { required } : {}
			}
		};
	},
	components: {
		VueMaskedInput,
		VueCaptcha,
		VueFormNotification,
		VuePasswordInput,
		VueErrorMessage,
		TechnicalError
	},
	methods: {
		submitLoginForm(e) {
			this.$v.$touch();
			if (this.$v.$invalid) {
				this.jumpToFirstError();
				e.preventDefault();
			} else {
				this.sendingForm = true;
			}
		},
		getCaptcha() {
			this.captcha.captchaKey = window.context.captcha.captchaKey;
			this.captcha.htmlFragment = window.context.captcha.htmlFragment;
			if (!this.captcha.captchaKey) {
				this.technicalError = true;
			}
		},
		resetCaptchaError() {
			this.captchaError = false;
		},
		handleUrlError() {
			const params = query.parse(location.search.substring(1));
			if (params[ERROR_STATUSES.INVALID_LOGIN] == 'true') {
				this.loginError = true;
			} else if (params[ERROR_STATUSES.USER_LOCKED] == 'true') {
				this.accountBlocked = true;
			} else if (params[ERROR_STATUSES.INVALID_STATUS] == 'true') {
				this.statusError = true;
			} else if (params[ERROR_STATUSES.CAPTCHA_ERROR] == 'true') {
				this.captchaError = true;
			} else {
				this.technicalError = Object.values(ERROR_STATUSES).some((status) => params[status] == 'true');
			}
		}
	},
	created() {
		this.handleUrlError();
		this.getCaptcha();
	}
};
</script>
<style src="@ui-common/vue-captcha/dist/vue-captcha.css"></style>
<style lang="scss" scoped>
@import '@ui-common/sf-style-utils/lib/helpers/_colors.scss';
@import '@ui-common/sf-style-utils/lib/helpers/_queries.scss';

.login-form {
	background-color: $text-inverse;
	border-radius: 40px;
	box-shadow: 0 16px 48px rgba(73, 92, 136, 0.15);
	padding: 24px 16px;
	position: relative;
	z-index: 1;

	@include media('<tablet') {
		margin-left: 8px;
		margin-right: 8px;
	}

	@include media('>=tablet') {
		max-width: 404px;
		padding: 32px;
	}

	@include media('>=desktop') {
		max-width: 504px;
	}

	@include media('>=desktop-lg') {
		max-width: 544px;
	}
}

.login-form > *[class^='sf-heading'] {
	@include media('<tablet') {
		font-size: 24px;
		line-height: 32px;
	}
}

.login-form ::v-deep input[type='password'] {
	letter-spacing: 0;
}

.login-form .form-row {
	padding: 0;
}

.login-form .form-row:last-of-type {
	margin-bottom: 40px;
}

.login-form .form-row .form-col:first-of-type {
	margin-right: 0;

	@include media('>=tablet') {
		max-width: 122px;
	}

	@include media('>=desktop') {
		max-width: 184px;
	}

	@include media('>=desktop-lg') {
		max-width: 224px;
	}
}

.login-form .form-row .form-col:first-of-type + .form-col {
	@include media('>=tablet') {
		min-width: 216px;
	}

	@include media('>=desktop') {
		max-width: 256px;
	}
}

.login-form .sf-form-notification,
.login-form > *[class^='sf-heading'] {
	margin-bottom: 16px;

	@include media('>=tablet') {
		margin-bottom: 32px;
	}
}

.login-form .sf-form-notification.sf-form-notification--icon-both ::v-deep .sf-form-notification__title {
	@include media('>=tablet') {
		display: block;
	}
}

.login-form .sf-form-notification ::v-deep .sf-link {
	color: $text-primary;
	text-decoration: underline;
}

.login-form .sf-text:last-of-type {
	margin-bottom: 0;
}

.login-form .form-row .sf-btn {
	width: 100%;
}

.login-form form + *[class^='sf-heading'] {
	margin-bottom: 8px;

	@include media('<tablet') {
		font-size: 18px;
		line-height: 24px;
		margin-bottom: 16px;
	}
}

.login-form__forgotten-pass-link {
	display: inline-block;
	margin-top: 8px;
}

.login-form__registration-link {
	margin-top: 24px;
}
</style>
